import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

import { WebhookService } from '../../../services/webhook/webhook.service';
import { LoggingService } from '../../../services/shared/logging/logging.service';
import { DialogService } from '../../../services/shared/dialog/dialog.service';
import {
  WebhookSubscription,
  WebhookType,
  WebhookTypeLabels,
  WebhookTypeDescriptions,
  WebhookTypeTriggerEvents,
  CreateWebhookSubscriptionRequest,
  UpdateWebhookSubscriptionRequest,
  TestWebhookResult,
  WebhookTypeConfiguration,
} from '../../../models/webhook.models';

interface WebhookFormData {
  clientName: string;
  endpointUrl: string;
  secretKey: string;
  webhookType: WebhookType;
}

@Component({
  selector: 'app-webhooks',
  standalone: true,
  imports: [NgClass, FormsModule],
  templateUrl: './webhooks.component.html',
  styleUrls: ['./webhooks.component.scss'],
})
export class WebhooksComponent implements OnInit {
  private webhookService = inject(WebhookService);
  private loggingService = inject(LoggingService);
  private dialogService = inject(DialogService);
  private toastr = inject(ToastrService);

  // --- State ---
  readonly subscriptions = signal<WebhookSubscription[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly saving = signal(false);

  // --- Type Configuration state ---
  readonly typeConfigurations = signal<WebhookTypeConfiguration[]>([]);
  readonly typeConfigsLoading = signal(false);
  readonly togglingType = signal<WebhookType | null>(null);

  // --- Modal state ---
  readonly showModal = signal(false);
  readonly editingSubscription = signal<WebhookSubscription | null>(null);
  readonly formData = signal<WebhookFormData>({
    clientName: '',
    endpointUrl: '',
    secretKey: '',
    webhookType: WebhookType.Tournaments,
  });
  readonly formErrors = signal<Record<string, string>>({});

  // --- Filter ---
  readonly typeFilter = signal<WebhookType | null>(null);

  // --- Test state ---
  readonly testingSubscriptionId = signal<string | null>(null);
  readonly testResult = signal<TestWebhookResult | null>(null);
  readonly testResultSubscription = signal<WebhookSubscription | null>(null);

  // --- Enum helpers for template ---
  readonly webhookTypes = Object.values(WebhookType).filter(
    (v) => typeof v === 'number'
  ) as WebhookType[];

  readonly WebhookTypeLabels = WebhookTypeLabels;

  // --- Computed ---
  readonly isEditing = computed(() => this.editingSubscription() !== null);

  readonly filteredSubscriptions = computed(() => {
    const filter = this.typeFilter();
    const subs = this.subscriptions();
    if (filter === null) return subs;
    return subs.filter((s) => s.type === filter);
  });

  readonly activeCount = computed(
    () => this.subscriptions().filter((s) => s.isActive).length
  );

  readonly totalCount = computed(() => this.subscriptions().length);

  ngOnInit(): void {
    this.loggingService.info('WebhooksComponent', 'Initializing Webhooks component');
    this.loadSubscriptions();
    this.loadTypeConfigurations();
  }

  // --- Data loading ---

  loadSubscriptions(): void {
    this.loading.set(true);
    this.error.set(null);

    firstValueFrom(this.webhookService.getAll()).then(
      (result) => {
        this.subscriptions.set(result);
        this.loading.set(false);
        this.loggingService.debug(
          'WebhooksComponent',
          `Loaded ${result.length} webhook subscriptions`
        );
      },
      (error) => {
        this.loading.set(false);
        const message = this.extractErrorMessage(error);
        this.error.set(message);
        this.loggingService.error(
          'WebhooksComponent',
          'Failed to load webhook subscriptions',
          error
        );
      }
    );
  }

  // --- Type Configuration ---

  loadTypeConfigurations(): void {
    this.typeConfigsLoading.set(true);
    firstValueFrom(this.webhookService.getTypeConfigurations()).then(
      (result) => {
        this.typeConfigurations.set(result);
        this.typeConfigsLoading.set(false);
      },
      (error) => {
        this.typeConfigsLoading.set(false);
        this.loggingService.error(
          'WebhooksComponent',
          'Failed to load webhook type configurations',
          error
        );
      }
    );
  }

  async toggleType(config: WebhookTypeConfiguration): Promise<void> {
    this.togglingType.set(config.type);
    try {
      if (config.isEnabled) {
        await firstValueFrom(this.webhookService.disableType(config.type));
        this.toastr.success(`${config.typeName} webhooks disabled`, 'Type Disabled');
      } else {
        await firstValueFrom(this.webhookService.enableType(config.type));
        this.toastr.success(`${config.typeName} webhooks enabled`, 'Type Enabled');
      }
      this.loadTypeConfigurations();
    } catch (error) {
      this.toastr.error(
        `Failed to toggle ${config.typeName}: ${this.extractErrorMessage(error)}`,
        'Error'
      );
    } finally {
      this.togglingType.set(null);
    }
  }

  // --- Modal ---

  openCreateModal(): void {
    this.editingSubscription.set(null);
    this.formData.set({
      clientName: '',
      endpointUrl: '',
      secretKey: '',
      webhookType: WebhookType.Tournaments,
    });
    this.formErrors.set({});
    this.showModal.set(true);
  }

  openEditModal(subscription: WebhookSubscription): void {
    this.editingSubscription.set(subscription);
    this.formData.set({
      clientName: subscription.clientName,
      endpointUrl: subscription.endpointUrl,
      secretKey: '', // Secret is not returned from API, must be re-entered
      webhookType: subscription.type,
    });
    this.formErrors.set({});
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingSubscription.set(null);
    this.formErrors.set({});
  }

  updateFormField(field: keyof WebhookFormData, value: string | WebhookType): void {
    this.formData.update((current) => ({ ...current, [field]: value }));
    // Clear error for this field
    this.formErrors.update((errors) => {
      const updated = { ...errors };
      delete updated[field];
      return updated;
    });
  }

  // --- Validation ---

  private validateForm(): boolean {
    const data = this.formData();
    const errors: Record<string, string> = {};

    if (!data.clientName.trim()) {
      errors['clientName'] = 'Client name is required';
    }

    if (!data.endpointUrl.trim()) {
      errors['endpointUrl'] = 'Endpoint URL is required';
    } else {
      try {
        const url = new URL(data.endpointUrl);
        if (!['http:', 'https:'].includes(url.protocol)) {
          errors['endpointUrl'] = 'URL must use http or https protocol';
        }
      } catch {
        errors['endpointUrl'] = 'Please enter a valid URL';
      }
    }

    if (!data.secretKey.trim()) {
      errors['secretKey'] = 'Secret key is required';
    }

    this.formErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  // --- Save ---

  async saveSubscription(): Promise<void> {
    if (!this.validateForm()) return;

    this.saving.set(true);
    const data = this.formData();

    try {
      if (this.isEditing()) {
        const id = this.editingSubscription()!.id;
        const request: UpdateWebhookSubscriptionRequest = {
          clientName: data.clientName.trim(),
          endpointUrl: data.endpointUrl.trim(),
          secretKey: data.secretKey.trim(),
        };
        await firstValueFrom(this.webhookService.update(id, request));
        this.toastr.success(
          `Webhook subscription "${data.clientName}" updated`,
          'Subscription Updated'
        );
      } else {
        const request: CreateWebhookSubscriptionRequest = {
          clientName: data.clientName.trim(),
          endpointUrl: data.endpointUrl.trim(),
          secretKey: data.secretKey.trim(),
          webhookType: data.webhookType,
        };
        await firstValueFrom(this.webhookService.create(request));
        this.toastr.success(
          `Webhook subscription "${data.clientName}" created`,
          'Subscription Created'
        );
      }
      this.closeModal();
      this.loadSubscriptions();
    } catch (error) {
      this.toastr.error(
        `Failed to save subscription: ${this.extractErrorMessage(error)}`,
        'Error'
      );
      this.loggingService.error(
        'WebhooksComponent',
        'Failed to save webhook subscription',
        error
      );
    } finally {
      this.saving.set(false);
    }
  }

  // --- Delete ---

  async deleteSubscription(subscription: WebhookSubscription): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      `Are you sure you want to delete the webhook subscription "${subscription.clientName}" (${WebhookTypeLabels[subscription.type]})?`,
      {
        title: 'Delete Webhook Subscription',
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
      }
    );

    if (!confirmed) return;

    try {
      await firstValueFrom(this.webhookService.delete(subscription.id));
      this.toastr.success(
        `Webhook subscription "${subscription.clientName}" deleted`,
        'Subscription Deleted'
      );
      this.loadSubscriptions();
    } catch (error) {
      this.toastr.error(
        `Failed to delete subscription: ${this.extractErrorMessage(error)}`,
        'Error'
      );
      this.loggingService.error(
        'WebhooksComponent',
        'Failed to delete webhook subscription',
        error
      );
    }
  }

  // --- Test ---

  async testSubscription(subscription: WebhookSubscription): Promise<void> {
    this.testingSubscriptionId.set(subscription.id);
    this.testResult.set(null);
    this.testResultSubscription.set(null);

    try {
      const result = await firstValueFrom(this.webhookService.test(subscription.id));
      this.testResult.set(result);
      this.testResultSubscription.set(subscription);

      if (result.success) {
        this.toastr.success(
          `Test payload delivered to "${subscription.clientName}" (${result.statusCode}, ${result.elapsedMs}ms)`,
          'Test Successful'
        );
      } else {
        this.toastr.warning(
          `Test payload failed for "${subscription.clientName}": ${result.errorMessage ?? 'Unknown error'}`,
          'Test Failed'
        );
      }
    } catch (error) {
      this.toastr.error(
        `Failed to test subscription: ${this.extractErrorMessage(error)}`,
        'Error'
      );
      this.loggingService.error(
        'WebhooksComponent',
        'Failed to test webhook subscription',
        error
      );
    } finally {
      this.testingSubscriptionId.set(null);
    }
  }

  dismissTestResult(): void {
    this.testResult.set(null);
    this.testResultSubscription.set(null);
  }

  // --- Filter ---

  onTypeFilterChange(value: string): void {
    if (value === 'all') {
      this.typeFilter.set(null);
    } else {
      this.typeFilter.set(parseInt(value, 10) as WebhookType);
    }
  }

  // --- Utility ---

  getWebhookTypeLabel(type: WebhookType): string {
    return WebhookTypeLabels[type] ?? 'Unknown';
  }

  getWebhookTypeDescription(type: WebhookType): string {
    return WebhookTypeDescriptions[type] ?? '';
  }

  getWebhookTypeTriggerEvents(type: WebhookType): string[] {
    return WebhookTypeTriggerEvents[type] ?? [];
  }

  formatDate(iso: string | null): string {
    if (!iso) return '—';
    try {
      const normalized = iso.endsWith('Z') ? iso : iso + 'Z';
      const date = new Date(normalized);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    } catch {
      return iso;
    }
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'object' && error !== null && 'message' in error) {
      return String((error as Record<string, unknown>)['message']);
    }
    return 'An unexpected error occurred';
  }
}
